import React, {useEffect, useState, useMemo} from 'react'
import {Grid, Stack, Typography} from '@mui/material'
import FormTextField from 'src/components/ui/TextField'
import AutoComplete, {AutocompleteOption} from 'src/components/ui/AutoComplete'
import {useFormContext} from 'react-hook-form'

export interface Bank {
  id: string
  name: string
  nameEn: string
  bankBranches: Branch[]
}

export interface Branch {
  id: string
  bankId: string
  code: number
  name: string
  nameEn: string
}

interface BankDetailsProps {
  banks: Bank[]
}

const BankDetails = ({banks}: BankDetailsProps) => {
  const {setValue, trigger, watch} = useFormContext()
  const currentBankId = watch('association.bankId')
  const currentBankName = watch('association.bank')

  const selectedBank = useMemo(() => {
    // Try by ID first (when ID is valid and non-zero)
    if (currentBankId) {
      const bank = banks?.find(b => Number(b.id) === Number(currentBankId))
      if (bank)
        return {
          id: bank.id,
          label: bank.id + ' - ' + bank.name,
          value: bank.name
        }
    }
    // Fallback: find by name (e.g. when bankId is 0 from external data source)
    if (currentBankName) {
      const bank = banks?.find(b => b.name === currentBankName)
      if (bank)
        return {
          id: bank.id,
          label: bank.id + ' - ' + bank.name,
          value: bank.name
        }
    }
    return null
  }, [currentBankId, currentBankName, banks])

  const [bankBranches, setBankBranches] = useState<any[]>([])
  const currentBranchId = watch('association.branchId')
  const currentBranchName = watch('association.branch')

  const selectedBranchOption = useMemo(() => {
    if (!bankBranches.length) return null
    // Exact name match
    const exact = bankBranches.find(b => b.value === currentBranchName)
    if (exact) return exact
    // Match by branchId (code)
    if (currentBranchId) {
      const byId = bankBranches.find(
        b => Number(b.id) === Number(currentBranchId)
      )
      if (byId) return byId
    }
    // Fallback: branch name from external source may include the code (e.g. "מרכז שירות חולון 61")
    if (currentBranchName) {
      const codeMatch = currentBranchName.match(/(\d+)/)
      if (codeMatch) {
        const code = parseInt(codeMatch[1])
        const byCode = bankBranches.find(b => Number(b.id) === code)
        if (byCode) return byCode
      }
    }
    return null
  }, [currentBranchName, currentBranchId, bankBranches])

  const defaultBranch = useMemo(
    () => ({
      id: '-1',
      bankId: selectedBank?.id,
      code: -1,
      name: 'אין סניפים לבנק זה',
      nameEn: 'bank without branch',
      branchName: ''
    }),
    [selectedBank?.id]
  )

  useEffect(() => {
    const bankBranches = (
      banks?.find((b: Bank) => b.id === selectedBank?.id)?.bankBranches
        .length === 0
        ? [defaultBranch]
        : banks
            ?.find(b => b.id === selectedBank?.id)
            ?.bankBranches.sort((a, b) => a.code - b.code) || []
    ).map((item: any) => ({
      id: item.code,
      label: (item.code ? item.code : '') + ' ' + item.name,
      value: item.name
    }))
    setBankBranches(bankBranches)
  }, [selectedBank, banks, defaultBranch])

  const bankNames = Array.from(
    new Map(
      banks.map((item: Bank) => [
        item.id,
        {id: item.id, label: item.id + ' - ' + item.name, value: item.name}
      ])
    ).values()
  )

  const revalidate = (fieldId: string) => {
    setTimeout(() => {
      trigger(fieldId)
    }, 100)
  }

  const onChangeBankName = (newValue: AutocompleteOption | null) => {
    const sBank = banks?.find(b => b.id === newValue?.id)
    if (newValue) {
      setValue('association.bank', sBank?.name)
      setValue('association.bankId', Number(sBank?.id))
      revalidate('association.bank')
    } else {
      setValue('association.bank', undefined)
      setValue('association.bankId', undefined)
      revalidate('association.bank')
    }

    setValue('association.branch', undefined)
    setValue('association.branchId', undefined)
    revalidate('association.branch')
  }

  const onChangeBranchName = (newValue: AutocompleteOption | null) => {
    const branch =
      banks
        .find(b => b.id === selectedBank?.id)
        ?.bankBranches.find(br => br.code === parseInt(newValue?.id || '')) ||
      defaultBranch
    setValue('association.branch', branch?.name)
    setValue('association.branchId', Number(branch?.code))
    revalidate('association.branch')
  }

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="h6">פרטי חשבון:</Typography>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <FormTextField
            name="association.accountOwner"
            label="שם בעל החשבון"
          />
        </Grid>
        <Grid item xs={3}>
          <AutoComplete
            options={bankNames}
            name="association.bank"
            label={'בנק'}
            onChange={onChangeBankName}
            getValue={o => o?.value}
          />
        </Grid>
        <Grid item xs={3}>
          <AutoComplete
            options={bankBranches}
            name="association.branch"
            label={'סניף'}
            onChange={onChangeBranchName}
            getValue={o => o?.value}
            selectedValue={selectedBranchOption}
          />
        </Grid>
        <Grid item xs={3}>
          <FormTextField name="association.accountNumber" label="מס’ חשבון" />
        </Grid>
      </Grid>
    </Stack>
  )
}

export default BankDetails
